import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, HeartHandshake, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const Shell = ({ testid, icon, title, text, children }) => (
  <main data-testid={testid} className="grid min-h-[90vh] place-items-center px-5 pt-28">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-xl text-center">
      <div className="mx-auto grid h-20 w-20 place-items-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37]">{icon}</div>
      <h1 data-testid={`${testid}-title`} className="font-display mt-8 text-5xl font-semibold text-[#F4EEE2] md:text-6xl">{title}</h1>
      <p className="mt-5 text-base leading-relaxed text-[#C9D0DB]">{text}</p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">{children}</div>
    </motion.div>
  </main>
);

const Btns = () => {
  const { t } = useLang();
  return (
    <>
      <Link to="/donate" data-testid="pay-again-btn" className="rounded-full border border-[#F4EEE2]/20 px-6 py-3 font-ui text-sm font-semibold text-[#F4EEE2] hover:border-[#D4AF37]">{t("pay.again")}</Link>
      <Link to="/" data-testid="pay-home-btn" className="rounded-full bg-[#D4AF37] px-6 py-3 font-ui text-sm font-semibold text-[#0B111E]">{t("pay.home")}</Link>
    </>
  );
};

export function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sid) {
      setState("failed");
      return undefined;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data?.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data?.payment_status)) return setState("failed");
      } catch {
        /* keep polling */
      }
      if (tries >= 10) return setState("failed");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  if (state === "checking") return <Shell testid="payment-checking" icon={<Loader2 className="h-8 w-8 animate-spin" />} title={t("pay.checking")} text="" />;
  if (state === "paid") return <Shell testid="payment-success" icon={<HeartHandshake className="h-8 w-8" />} title={t("pay.thanks")} text={t("pay.thanksText")}><Btns /></Shell>;
  return <Shell testid="payment-failed" icon={<XCircle className="h-8 w-8" />} title={t("pay.failed")} text={t("pay.failedText")}><Btns /></Shell>;
}

export function PaymentCancel() {
  const { t } = useLang();
  return <Shell testid="payment-cancel" icon={<XCircle className="h-8 w-8" />} title={t("pay.cancel")} text={t("pay.cancelText")}><Btns /></Shell>;
}
